/**
 * Created by Jaap on 01/10/19.
 *
 * This is the interface class for the new customizable interface
 *
 * @author Jaap
 * @since 01/10/19
 *
 */
import Renderer from "../Renderer";
import Cookies from "js-cookie";
import Tooltip from "../utils/Tooltip";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import iNoBounce from "inobounce";
import Utils from "../utils/Utils";

export default class CustomizableInterface {
    constructor(ic) {
        this.iNobounce = iNoBounce;
        this.iNobounce.disable();

        this.mainContainer = document.querySelector(".main");
        this.viewerContainer = this.mainContainer.querySelector(".viewer");
        this.overviewContainer = this.mainContainer.querySelector(".overview");
        this.activeView = this.mainContainer.dataset["activeview"];
        this._legacy_issues = [];
        this.interfaceController = ic;

        this.navigationSpreadsContainer = document.getElementById("navigation-bar-spreads-list");
        this.accessibilityTocContainer = document.getElementById("TableOfContents");
        this.accessibilityBreadCrumbContainer = document.getElementById("breadCrumb");

        let spreadsTemplate = document.getElementById("HB-navigation-bar-spread");
        this.HBspreadsTemplate = spreadsTemplate && spreadsTemplate.innerHTML;
        let accessibilityToc = document.getElementById("HB-accessibility-toc-item");
        this.HBaccessibilityToc = accessibilityToc && accessibilityToc.innerHTML;
        let accessibilityBreadCrumb = document.getElementById("HB-accessibility-breadcrumb");
        this.HBaccessibilityBreadCrumb = accessibilityBreadCrumb && accessibilityBreadCrumb.innerHTML;

        this._pageNumberIndicator = document.getElementById("page-number-indicator");
        this._issueIndicatorLine = document.getElementById("issue-indicator-line");
        this._pageTitleContainer = document.getElementById("page-title-container");

        this.viewerContainer.addEventListener("animationend", (event) => {
            if (event.target && event.target.classList && event.target.classList.contains("viewer")) {
                switch (event.animationName) {
                    case "moveFromRight":
                    case "fadeInViewer":
                        this._setActiveView("viewer");
                        break;
                    case "moveToRight":
                        this._setActiveView("overview");
                        this.interfaceController.fc.clearStages();
                        break;
                }
            }
        });

        this.bindSpreadsListScroll();

        let fullscreen = document.getElementById("toggle-fullscreen");
        fullscreen &&
            fullscreen.addEventListener("click", () => {
                if (!document.fullscreenElement) {
                    document.documentElement.requestFullscreen();
                } else {
                    if (document.exitFullscreen) {
                        document.exitFullscreen();
                    }
                }
            });

        const classicGoUp = document.getElementById("classic-go-up-button");
        const keyupHandler = (e) => {
            if ((e.which || e.keyCode) === 32 || (e.which || e.keyCode) === 13 || e.type === "mouseenter") {
                // e.preventDefault()
                e.stopPropagation();
                this.setSpreadsContainerOpen(classicGoUp);

                const focusTrap = Utils.trapFocus(
                    this.navigationSpreadsContainer,
                    this.navigationSpreadsContainer.querySelector(".active"),
                );
                const handleEscape = (event) => {
                    if ((event.which || event.keyCode) === 27) {
                        event.stopPropagation();
                        const basicGoUp = document.querySelector(".basic-go-up");
                        if (basicGoUp) {
                            basicGoUp.classList.add("disable-hover");
                            setTimeout(() => {
                                basicGoUp.classList.remove("disable-hover");
                            }, 1000); // timeout for animation to end.
                        }

                        Utils.releaseFocus(focusTrap);
                        document.removeEventListener("keyup", handleEscape);
                        this.setSpreadsContainerClosed(classicGoUp);
                    }
                };

                document.addEventListener("keyup", handleEscape);
            }
        };
        classicGoUp && classicGoUp.addEventListener("keyup", keyupHandler);

        const basicGoUp = document.querySelector(".basic-go-up");
        basicGoUp && basicGoUp.addEventListener("mouseenter", keyupHandler);
        basicGoUp &&
            basicGoUp.addEventListener("mouseleave", () => {
                this.setSpreadsContainerClosed(classicGoUp);
            });

        if (basicGoUp && window.matchMedia("(hover: none)").matches) {
            //bind click on basic go up
            basicGoUp.addEventListener("click", (e) => {
                console.log(e);
                e.preventDefault();
                this.navigationSpreadsContainer.classList.toggle("open");
            });
        }
    }

    setSpreadsContainerOpen(classicGoUp) {
        if (classicGoUp) {
            this.navigationSpreadsContainer.classList.add("open");
            classicGoUp.setAttribute("aria-expanded", "true");
            this.setTemplateMode(false);
        }
    }

    setSpreadsContainerClosed(classicGoUp) {
        if (classicGoUp) {
            this.navigationSpreadsContainer.classList.remove("open");
            classicGoUp.setAttribute("aria-expanded", "false");
            classicGoUp.focus(); // focus back to the go-up-button
            this.restoreTemplateMode();
        }
    }

    setLegacy(legacy_issues) {
        this._legacy_issues = legacy_issues;
    }

    setCategoryActive(id) {
        document.querySelectorAll(`a[data-cat_id]`).forEach((el) => {
            if (el.dataset.cat_id == id) {
                el.classList.add("active");
                // el.setAttribute('aria-selected', 'true');
            } else {
                el.classList.remove("active");
                // el.setAttribute('aria-selected', 'false');
            }
        });
    }

    doLazyLoad(selector) {
        $(selector).each(function (index, element) {
            $(element).attr("src", $(element).data("src")).fadeIn(200).css("opacity", 1);
        });
    }

    /**
     * Renders the spreads from given issue with handlebars
     *
     * @public
     */
    renderHomepage(issues) {
        const source = $("#HB-overview-issue-list").html();
        let html = Renderer.renderSource(source, {
            issues: issues,
            data_url: window.data_domain,
            texts: window.interface_settings.settings.text,
            show_download: window.interface_settings.settings.show_download,
        });
        Renderer.nativeInject("overview-issue-list", html);
    }

    renderRecent(issues) {
        if (self.primary_domain) {
            return;
        }
        const source = $("#HB-overview-issue-list").html();
        let html = Renderer.renderSource(source, {
            issues: issues,
            data_url: window.data_domain,
            texts: window.interface_settings.settings.text,
            show_download: window.interface_settings.settings.show_download,
        });
        Renderer.nativeInject("recent-issues-list", html);
    }

    /**
     * Open the spreads viewer with the owl-carousel in it.
     */
    showViewer(switchIssue) {
        this.transitionToViewer(switchIssue);
    }

    renderSpreadNavigationList(spreads, issue) {
        if (this._currentNavigationRenderedIssueId === issue.issue_id) {
            return false;
        }

        if (this.navigationSpreadsContainer && this.HBspreadsTemplate) {
            let context = {
                LANG: location.pathname.split("/").splice(1)[0],
                DATA_URL: window.data_domain,
                first: spreads[0],
                spreads: spreads,
                issue_title: issue.issue_title,
                thumbrow_close_text: window.interface_settings?.settings?.text?.thumbrow_close_text,
            };
            this.navigationSpreadsContainer.innerHTML = sanitizeHtml(
                Renderer.renderSource(this.HBspreadsTemplate, context),
            );
        }

        if (this.accessibilityTocContainer && this.HBaccessibilityToc) {
            this.accessibilityTocContainer.innerHTML = sanitizeHtml(
                Renderer.renderSource(this.HBaccessibilityToc, { js_spreads: spreads }),
            );
        }

        this._currentNavigationRenderedIssueId = issue.issue_id;
    }

    showViewerDirect() {
        setTimeout(() => {
            this.bindTooltips();
        }, 1000);
    }

    transitionToViewer(switchIssue) {
        if (this.activeView !== "viewer" || switchIssue) {
            let transition = "transitionToViewer";
            // switchIssue && (transition = 'transitionToViewerCrossIssue');

            window.scrollTo({ top: 0 });
            this._setActiveView(transition);

            setTimeout(() => {
                this.bindTooltips();
            }, 2000);
        }
    }

    transitionToOverview() {
        if (this.activeView !== "overview" && this.overviewContainer) {
            this._setActiveView("transitionToOverview");
            this.interfaceController.updateBodyScrollLock(false);
            this.setTemplateMode(false);
        }
    }

    _setActiveView(activeview) {
        this.mainContainer.dataset["activeview"] = activeview;
        this.activeView = activeview;
    }

    updateActiveSpread(spread, totalSpreads) {
        let index = spread.page_index;

        if (this.navigationSpreadsContainer && this.navigationSpreadsContainer.classList.contains("open")) {
            this.navigationSpreadsContainer.classList.remove("open");
        }

        if (this._pageNumberIndicator) {
            this._pageNumberIndicator.innerHTML = `${index} / ${totalSpreads}`;
            const chars = { $1: index, $2: totalSpreads };
            window.interface_settings?.settings?.text?.aria_page_number &&
                this._pageNumberIndicator.setAttribute(
                    "aria-label",
                    window.interface_settings.settings.text.aria_page_number.replace(/(\$1)|(\$2)/g, (m) => chars[m]),
                );
        }
        if (this._issueIndicatorLine) {
            this._issueIndicatorLine.style.width = `${(index / totalSpreads) * 100}%`;
        }
        if (this._pageTitleContainer) {
            this._pageTitleContainer.innerText = spread.spread_title;
        }

        let activeSpreadId;
        if (
            !this.interfaceController.dataHelper.hasMainSpreadNavigation(spread.js_issue_issue_id) &&
            !this.interfaceController.dataHelper.isMainSpread(spread.js_issue_issue_id, spread.spread_id)
        ) {
            activeSpreadId = this.interfaceController.dataHelper.findMainSpreadIdOfSubSpread(
                spread.js_issue_issue_id,
                spread.spread_id,
            );
        } else {
            activeSpreadId = spread.spread_id;
        }

        let active = [];
        document.querySelectorAll("[data-spreadlink]").forEach((element) => {
            if (parseInt(element.dataset.spreadlink) === activeSpreadId) {
                element.classList.add("active");
                element.setAttribute("aria-current", "page");
                active.push(element);
            } else {
                element.classList.remove("active");
                element.removeAttribute("aria-current");
            }
        });

        //center active in spread scroll list
        // active.length > 0 && active[0].scrollIntoView({block: 'center', inline: 'center', behavior: 'smooth'}) // no support for the options in IE
        active.length > 0 &&
            active.forEach((entry) => {
                if (entry.offsetParent && !entry.dataset.noscroll) {
                    entry.scrollIntoView({ block: "center", inline: "center" }); // no support for the options in IE
                    // entry.scrollIntoView({block: 'center', inline: 'center', behavior: 'smooth'}) // no support for the options in IE
                }
            });
    }

    bindTooltips() {
        if (!this._tooltipsBound) {
            this._tooltipsBound = true;

            if (Cookies.get("hideNavigationTippy") !== "true" && window.interface_settings.settings.show_help_tooltip) {
                const helpTarget = document.querySelector(".show-help-tooltip");
                if (helpTarget) {
                    const helpTip = Tooltip.createTooltip(helpTarget, {
                        close: true,
                        title: window.interface_settings.settings?.text?.navigation_tooltip_title || "Navigation",
                        text:
                            window.interface_settings.settings?.text?.navigation_tooltip_description ||
                            "Please click here to navigate through the pages of this magazine.",
                    });
                    $(helpTip.element).on("click", ".close_btn", () => {
                        helpTip.hide();
                        Cookies.set("hideNavigationTippy", "true", { expires: 365 });
                    });
                    helpTip.show();
                }
            }

            if (
                window.interface_settings.settings.show_register_tooltip &&
                Cookies.get("hideRegisterTippy") !== "true"
            ) {
                this._registerPopupListener = (event) => {
                    this._startedSpreadCount.indexOf(event.detail.spreadId) === -1 &&
                        this._startedSpreadCount.push(event.detail.spreadId);

                    if (this._startedSpreadCount.length === 2) {
                        window.removeEventListener("SPREAD_STARTED", this._registerPopupListener);

                        this.showRegisterPopup();
                    }
                };
                this._startedSpreadCount = [];
                window.addEventListener("SPREAD_STARTED", this._registerPopupListener);
            } else {
                let notifButton = document.querySelector('[data-tooltip-btn="register"]');
                notifButton && (notifButton.style.display = "none");
            }
        }
    }

    showRegisterPopup() {
        const registerTarget = document.getElementById("registerPopupContainer");
        if (registerTarget) {
            const options = {
                close: true,
                title: window.interface_settings.settings?.text?.register_tooltip_title,
                text: window.interface_settings.settings?.text?.register_tooltip_description,
                buttons: [],
            };

            if (window.interface_settings.settings.show_register_tooltip) {
                options.buttons.push({
                    text: window.interface_settings.settings?.text?.register_tooltip_button_newsletter,
                    icon: "mail",
                });
            }

            const registerTip = Tooltip.createPopup(options);
            document.getElementById("registerPopupContent").appendChild(registerTip);

            $(registerTip).on("click", ".close_btn", () => {
                registerTarget.classList.add("closed");
                registerTip.removeAttribute("aria-expanded");
                Cookies.set("hideRegisterTippy", "true", { expires: 365 });
                setTimeout(() => {
                    registerTarget.classList.add("disabled");
                }, 500);
            });
            $(registerTip).on("click", "[data-tooltip-btn]", (e) => {
                switch (e.currentTarget.dataset.tooltipBtn) {
                    case "mail":
                        this.interfaceController.sidemenu.setActiveTab("register").openMenu(e.detail < 1);
                        break;
                    case "register":
                        // was web push notifications
                        break;
                }
                registerTarget.classList.add("closed");
                registerTip.removeAttribute("aria-expanded");
                Cookies.set("hideRegisterTippy", "true", { expires: 365 });
                setTimeout(() => {
                    registerTarget.classList.add("disabled");
                }, 500);
            });

            registerTarget.classList.add("shown");

            //set focus in the popup for A11y
            const close = registerTip.querySelector(".close_btn");
            close && close.focus();
        }
    }

    /**
     * Bind a mousewheel listener for the horizontal spreads list when it needs one.
     * Checks if the interface has a horizontal spreads list, only the classic- and textmenu-interface have one and only
     * when positioned at the top or the bottom of the screen.
     */
    bindSpreadsListScroll() {
        if (this.navigationSpreadsContainer && window.innerWidth > 767) {
            // dont bind scroll for mobile view which is vertical
            if (
                this.viewerContainer.classList.contains("interface-classic") ||
                this.viewerContainer.classList.contains("interface-textmenu")
            ) {
                if (
                    this.viewerContainer.classList.contains("top") ||
                    this.viewerContainer.classList.contains("bottom")
                ) {
                    this.navigationSpreadsContainer.addEventListener(
                        "wheel",
                        (e) => {
                            e.stopPropagation();
                            // e.preventDefault();
                            const delta = e.wheelDelta ? e.wheelDelta : -e.detail * 25 || -e.deltaY;
                            this.navigationSpreadsContainer.scrollLeft -= delta;
                        },
                        { passive: true },
                    );
                }
            }
        }
    }

    setOverviewTitle(title) {
        this.overviewContainer.querySelector(".overview-cat-heading").innerText = title;
        this.overviewContainer.querySelector(".overview-item-container")?.setAttribute?.("aria-label", title);
    }

    setTemplateMode(bool, saveCurrentMode) {
        if (bool) {
            document.documentElement.classList.add("maglrTemplateMode");
            this.iNobounce.enable();
        } else {
            document.documentElement.classList.remove("maglrTemplateMode");
            this.iNobounce.disable();
        }
        if (saveCurrentMode) {
            this._savedTemplateMode = bool;
        }
    }

    restoreTemplateMode() {
        this.setTemplateMode(this._savedTemplateMode);
    }

    updateBreadCrumb(breadCrumb) {
        if (this.accessibilityBreadCrumbContainer && this.HBaccessibilityBreadCrumb) {
            this.accessibilityBreadCrumbContainer.innerHTML = sanitizeHtml(
                Renderer.renderSource(this.HBaccessibilityBreadCrumb, { breadcrumbPath: breadCrumb }),
            );
        }
    }

    toggleInterface(hideInterface) {
        if (hideInterface) {
            this.viewerContainer.classList.add("hide-interface-menu");
        } else {
            this.viewerContainer.classList.remove("hide-interface-menu");
        }
    }
}
